/**
 * Section 2: Property Access
 */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Input, Select, Textarea } from '@ox/visits-ui/components/shared/FormComponents';
import { isOccupiedWalk } from '@ox/visits-ui/utils/validation';

export function Section2PropertyAccess() {
  const { draft, formData, updateField } = useForm();
  // TXN-3408: an occupied walk has no lockbox, so its lockbox and key questions are hidden.
  const occupied = isOccupiedWalk(draft?.parentVisitType);

  const handleAccessChange = useCallback(
    (value: string) => updateField('were_able_to_access_property', value),
    [updateField],
  );

  const handleFunctionalKeysChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        if (+formData.number_of_functional_front_entry_keys > 3) {
          return;
        }
        value = '4';
      }
      updateField('number_of_functional_front_entry_keys', value);
    },
    [updateField, formData.number_of_functional_front_entry_keys],
  );

  const handleGatedCommunityChange = useCallback(
    (value: string) => updateField('gated_community', value),
    [updateField],
  );

  const handleGateAccessInfoChange = useCallback(
    (value: string) => updateField('gate_access_info', value),
    [updateField],
  );

  const handleNonFunctionalKeysChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('non_functional_keys', 'Other: ');
      } else {
        updateField('non_functional_keys', value);
      }
    },
    [updateField],
  );

  const handleNonFunctionalKeysOtherChange = useCallback(
    (value: string) => updateField('non_functional_keys', 'Other: ' + value),
    [updateField],
  );

  const handleWorkingCodesChange = useCallback(
    (value: string) => updateField('working_codes', value),
    [updateField],
  );

  const handleLockboxLocationChange = useCallback(
    (value: string) => updateField('lockbox_location', value),
    [updateField],
  );

  return (
    <div className="form-section">
      <h2>Property Access</h2>

      <p>
        <strong>Note:</strong> If you were unable to access the property, you'll be asked to
        describe the access issues in the next section.
      </p>

      <Select
        label="Were you able to gain access to the property?"
        name="were_able_to_access_property"
        value={formData.were_able_to_access_property || ''}
        onChange={handleAccessChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />

      {formData.were_able_to_access_property === 'Yes' && (
        <>
          {occupied && (
            <p>
              <strong>Resident occupied, no lockbox.</strong> The resident let you in, so there are no
              lockbox or key questions for this visit.
            </p>
          )}

          {!occupied && (
            <Select
              label="How many functional front entry keys are present in the lockbox?"
              name="number_of_functional_front_entry_keys"
              value={(+formData.number_of_functional_front_entry_keys > 3 ? 'Other' : formData.number_of_functional_front_entry_keys) || ''}
              onChange={handleFunctionalKeysChange}
              options={[
                { label: 'Select...', value: '', disabled: true },
                { label: '0', value: '0' },
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: 'Other', value: 'Other' },
              ]}
              required
            />
          )}
          {!occupied && +formData.number_of_functional_front_entry_keys > 3 && (
            <Input
              label="Please specify the number of functional front entry keys"
              name="number_of_functional_front_entry_keys"
              type="number"
              value={formData.number_of_functional_front_entry_keys || ''}
              onChange={handleFunctionalKeysChange}
            />
          )}

          {!occupied && (
            <Select
              label="Are there any keys that are non-functional?"
              name="non_functional_keys"
              value={
                formData.non_functional_keys?.startsWith('Other:')
                  ? 'Other'
                  : formData.non_functional_keys || ''
              }
              onChange={handleNonFunctionalKeysChange}
              options={[
                { label: 'Select...', value: '', disabled: true },
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
                { label: 'Other', value: 'Other' },
              ]}
            />
          )}

          {!occupied && formData.non_functional_keys?.startsWith('Other:') && (
            <Input
              label="Please specify"
              name="non_functional_keys_other"
              value={formData.non_functional_keys.replace('Other: ', '')}
              onChange={handleNonFunctionalKeysOtherChange}
              placeholder="Please specify..."
            />
          )}

          <Input
            label="If you were given multiple access codes, which access code or codes gave you access to the property?"
            name="working_codes"
            value={formData.working_codes || ''}
            onChange={handleWorkingCodesChange}
            placeholder="Enter access codes"
          />

          {!occupied && (
            <Input
              label="Can you provide a short description of the lockbox location?"
              name="lockbox_location"
              value={formData.lockbox_location || ''}
              onChange={handleLockboxLocationChange}
              placeholder="EX: Garage trim, Front Door, Hose bib"
              required
            />
          )}

          <Select
            label="Is this property in a gated community?"
            name="gated_community"
            value={formData.gated_community || ''}
            onChange={handleGatedCommunityChange}
            options={[
              { label: 'Select...', value: '', disabled: true },
              { label: 'Yes', value: 'Yes' },
              { label: 'No', value: 'No' },
            ]}
            required
          />

          {formData.gated_community === 'Yes' && (<Textarea
            label="Gate Access Information - How do future visitors access this property?"
            name="gate_access_info"
            value={formData.gate_access_info || ''}
            onChange={handleGateAccessInfoChange}
            autoSize
            required
          />)}
        </>
      )}
    </div>
  );
}
